// Persistent banner shown at the top of MatterTabs when a timer is running/paused.
// Provides quick pause/resume/stop from any tab without switching to Billing or Tasks.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { TimeEntry } from '../../billing/types/billing.types';

interface ActiveTimerBannerProps {
  activeTimer: TimeEntry;
  onPause: (timeEntryId: string) => Promise<TimeEntry>;
  onResume: (timeEntryId: string) => Promise<TimeEntry>;
  onStop: (timeEntryId: string) => Promise<TimeEntry>;
  onComplete?: (entry: TimeEntry) => void;
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const ActiveTimerBanner: React.FC<ActiveTimerBannerProps> = ({
  activeTimer,
  onPause,
  onResume,
  onStop,
  onComplete,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRunning = activeTimer.timer_status === 'running';

  const calculateElapsed = useCallback(() => {
    const baseDuration = Number(activeTimer.duration || 0) * 3600;
    if (activeTimer.timer_status === 'paused') {
      return baseDuration;
    }
    if (activeTimer.timer_status === 'running' && activeTimer.timer_started_at) {
      const startedAt = new Date(activeTimer.timer_started_at).getTime();
      const currentSession = (Date.now() - startedAt) / 1000;
      return baseDuration + currentSession;
    }
    return baseDuration;
  }, [activeTimer]);

  useEffect(() => {
    setElapsed(calculateElapsed());
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setElapsed(calculateElapsed());
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, calculateElapsed]);

  const handlePause = async () => {
    try {
      setActionLoading(true);
      await onPause(activeTimer.time_entry_id);
    } catch { /* error handled by parent */ }
    finally { setActionLoading(false); }
  };

  const handleResume = async () => {
    try {
      setActionLoading(true);
      await onResume(activeTimer.time_entry_id);
    } catch { /* error handled by parent */ }
    finally { setActionLoading(false); }
  };

  const handleStop = async () => {
    try {
      setActionLoading(true);
      const finalEntry = await onStop(activeTimer.time_entry_id);
      onComplete?.(finalEntry);
    } catch { /* error handled by parent */ }
    finally { setActionLoading(false); }
  };

  return (
    <div className={`flex items-center justify-between px-4 py-2 text-sm border-b ${
      isRunning
        ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
        : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        {/* Pulsing dot */}
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          isRunning ? 'bg-green-500 animate-pulse' : 'bg-amber-500'
        }`} />

        {/* Clock */}
        <span className={`font-mono font-bold tabular-nums ${
          isRunning ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'
        }`}>
          {formatElapsed(elapsed)}
        </span>

        {/* Description */}
        <span className="text-gray-600 dark:text-gray-300 truncate">
          {activeTimer.description}
        </span>

        {/* Status label */}
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
          isRunning
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
        }`}>
          {isRunning ? 'Running' : 'Paused'}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
        {isRunning ? (
          <button
            onClick={handlePause}
            disabled={actionLoading}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/20 rounded hover:bg-amber-200 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
            Pause
          </button>
        ) : (
          <button
            onClick={handleResume}
            disabled={actionLoading}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/20 rounded hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
            Resume
          </button>
        )}
        <button
          onClick={handleStop}
          disabled={actionLoading}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/20 rounded hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 6h12v12H6z" />
          </svg>
          Stop
        </button>
      </div>
    </div>
  );
};

export default ActiveTimerBanner;
